import React from "react";
import styled, { css } from "styled-components";

import { ICustomer } from "src/core/customers/api/customers.api";
import useTerminology from "src/core/shared/hooks/useTerminology";
import {
  CustomerBranchFilterType,
  GlobalProductReplacementFilter
} from "src/core/products/api/products.api";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import {
  IOpenDayFilter,
  CreateOpenDayFilterAttributes
} from "src/modules/shutdowns/api/open-day.api";

import { Tooltip } from "../Tooltip";
import { Text } from "../Text";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import { Button } from "../Button";
import { CustomerBranchFilterAction } from "./CustomerBranchFilters";

interface CustomerBranchFilterRowProps {
  filter: IOpenDayFilter | GlobalProductReplacementFilter;
  setSelectedCustomer?: (customer: ICustomer| null) => void;
  setShowFiltersModal?: (show: boolean) => void;
  removeFilter: (filter: CreateOpenDayFilterAttributes | GlobalProductReplacementFilter) => void;
  className?: string;
  action: CustomerBranchFilterAction;
}

const CustomerBranchFilterRow: React.ReactFCWithChildren<CustomerBranchFilterRowProps> = ({
  filter, setShowFiltersModal, setSelectedCustomer, removeFilter, className, action
}) => {
  const tippyId = filter.branchId ? `branch-${filter.branchId}` : `customer-${filter.customerId}`;
  const terminology = useTerminology();
  const customerOrDrop = filter.branchId ? terminology?.drop.singular.text : terminology?.customer.singular.text;

  const tippyText = React.useMemo(() => {
    switch (action) {
      case CustomerBranchFilterAction.OpenDay:
        return filter.filterType === CustomerBranchFilterType.Exclude ? `This ${customerOrDrop} will be excluded from this open day and may not be able to place orders.` : `This ${customerOrDrop} will be allowed to place orders on this open day`;
      case CustomerBranchFilterAction.ProductReplacement:
        return filter.filterType === CustomerBranchFilterType.Exclude ? `This ${customerOrDrop} will be excluded from this product replacement and their orders will not be affected.` : `This ${customerOrDrop} will be involved in this product replacement and their orders will be affected`;
      case CustomerBranchFilterAction.LeadTimeOverride:
        return filter.filterType === CustomerBranchFilterType.Exclude ? `This ${customerOrDrop} will be excluded from this lead time override and their orders will not be affected.` : `This ${customerOrDrop} will be involved in this lead time override and their orders will be affected`;
    }
  }, [
    action,
    customerOrDrop,
    filter.filterType
  ]);

  return (
    <FilterRowContainer>
      <div className={`filter-row-main ${className}`}>
        <Tooltip
          id={tippyId}
          type="dark"
          place="right"
          getContent={() => (
            <>
              <Text color="white">
                {tippyText}
              </Text>
            </>
          )}
        />

        <div className="name-and-icon">
          {filter.customer && (
            <div
              data-tip
              data-for={tippyId}
              className="name-and-icon-inner"
            >
              <Icon icon={Icons.PEOPLE} />

              <Text>
                {`${filter.customer.name} (${filter.customer?.accountNumber})`}
              </Text>
            </div>
          )}

          {filter.branch && (
            <div
              data-tip
              data-for={tippyId}
              className="name-and-icon-inner"
            >
              <Icon icon={Icons.BRANCH} />

              <Text>
                {formatDropName(filter.branch)}
              </Text>
            </div>
          )}

          {filter.customerId && (
            <Button
              $buttonType="plain"
              icon={Icons.ADD}
              iconPosition="left"
              onClick={() => {
                if (setSelectedCustomer && setShowFiltersModal) {
                  setSelectedCustomer(filter.customer || null);
                  setShowFiltersModal(true);
                }
              }}
            >
              {`${filter.filterType === CustomerBranchFilterType.Exclude ? "Include" : "Exlcude"} some drops`}
            </Button>
          )}
        </div>

        <Icon
          className="remove-icon"
          icon={Icons.CLOSE}
          onClick={() => removeFilter(filter)}
        />
      </div>
    </FilterRowContainer>
  );
};

const FilterRowContainer = styled.div`
  ${({ theme }) => css`
    .remove-icon:hover {
      cursor: pointer;
    }

    .filter-row-main {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      border-bottom: 1px solid ${theme.colors.lightGrey};

      .name-and-icon {
        display: flex;
        align-items: center;

        .name-and-icon-inner {
          display: flex;
          align-items: center;

          > svg {
            margin-right: ${theme.spacing.margins.md}; 
          }
        }

      
      }

      &.${CustomerBranchFilterType.Exclude} {
        .name-and-icon {
          span {
            color: ${theme.colors.error};
          }

          .name-and-icon-inner svg {
            fill: ${theme.colors.error}!important;
          }
        }
      }

      &.${CustomerBranchFilterType.Include} {
        .name-and-icon {
          span {
            color: ${theme.colors.success};
          }

          .name-and-icon-inner svg {
            fill: ${theme.colors.success}!important; 
          }
        }
      }
    }
  `}
`;

export default CustomerBranchFilterRow;
